import { useState, useEffect } from "react";
import { visionURL } from "../config/environment";
import Jsona from "jsona";
import JsonApiService from "../services/jsonapiService";
import { uniqBy, sortBy } from "lodash";
import { courseSectionTypes } from "../constants";

const dataFormatter = new Jsona();

// Returns all other training objects related to a course through its items
const getRelatedTrainingObjects = (items, courseId) =>
  items
    ? uniqBy(
        items.reduce((current, { latestVersion }) => {
          const related =
            latestVersion && latestVersion.trainingObjects
              ? latestVersion.trainingObjects.filter(
                  ({ id }) => `${id}` !== courseId
                )
              : [];
          return [...current, ...related];
        }, []),
        "id"
      )
    : [];

const coursesApiService = async () =>
  new JsonApiService("courses", {
    baseUrl: `${visionURL}/admin_console/api/content`,
    credentials: "include"
  });

/**
 * Retrieves the items related to a course's pending assignment
 */
export const usePendingCourseItems = (
  courseId,
  withoutOverview = false,
  isPending
) => {
  const [isFetching, setIsFetching] = useState(false);
  const [courseItems, setCourseItems] = useState([]);
  const [relatedCourses, setRelatedCourses] = useState([]);
  const getPendingCourseItems = async () => {
    if (isPending && courseId) {
      setIsFetching(true);
      const service = await coursesApiService();
      const response = await service
        .where({ without_overview: withoutOverview, pending: true })
        .related(courseId, "items")
        .then(response => response.json());
      const items = dataFormatter.deserialize(response);
      setCourseItems(items);
      setRelatedCourses(getRelatedTrainingObjects(items, courseId));
      setIsFetching(false);
    }
  };

  useEffect(() => {
    getPendingCourseItems();
  }, [courseId, isPending]);

  return { courseItems, relatedCourses, isFetching };
};

export const useCourseDeployments = () => {
  const deployCourse = async (courseId, forceRestart) => {
    const service = await coursesApiService();
    const response = await service
      .createRelated(
        courseId,
        "deployments",
        JSON.stringify({
          forceRestart
        })
      )
      .then(response => response.json());
    return response;
  };

  return { deployCourse };
};

const getCourseSections = (sections, sectionType) => {
  return !!sections
    ? sortBy(
        sections
          .filter(courseSection => courseSection.sectionType === sectionType)
          .map(section => ({
            ...section,
            items: sortBy(
              section.shortCourseActivities.map(item => ({
                ...item,
                type: item.contentableType
              })),
              ["position"]
            )
          })),
        ["position"]
      )
    : [];
};

export const useCourseSections = courseId => {
  const [isFetching, setIsFetching] = useState(false);
  const [courseSections, setCourseSections] = useState([]);
  const [mandatorySections, setMandatorySections] = useState([]);
  const [optionalSections, setOptionalSections] = useState([]);

  const fetchCourseSections = async () => {
    if (courseId) {
      setIsFetching(true);
      const service = await coursesApiService();
      const response = await service
        .related(courseId, "course_sections")
        .then(response => response.json());
      const courseSections = dataFormatter.deserialize(response);
      setCourseSections(courseSections);
      setIsFetching(false);
    }
    return courseSections;
  };

  useEffect(() => {
    fetchCourseSections();
  }, [courseId]);

  useEffect(() => {
    const mandatoryCourseSections = getCourseSections(
      courseSections,
      courseSectionTypes.mandatory
    );
    const optionalCourseSections = getCourseSections(
      courseSections,
      courseSectionTypes.optional
    );
    setMandatorySections(mandatoryCourseSections);
    setOptionalSections(optionalCourseSections);
  }, [courseSections]);

  return {
    courseSections,
    mandatorySections,
    optionalSections,
    isFetching,
    fetchCourseSections
  };
};
